import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { DRAWING_COLORS } from './constants';
import type { BOMEntry, Accessory, ProductInfo } from '@shared/types';

const JP_FONT = "'Noto Sans JP', sans-serif";
const C = DRAWING_COLORS;

type BOMTableProps = {
  x: number;
  y: number;
  width: number;
  maxHeight: number;
  bom: BOMEntry[];
  accessories?: Accessory[];
  productInfo?: ProductInfo;
};

const COL_RATIOS = {
  number: 0.05,
  name: 0.14,
  spec: 0.35,
  quantity: 0.05,
  material: 0.13,
  dimensions: 0.16,
  notes: 0.12,
};

const HEADER_HEIGHT = 9;
const ROW_HEIGHT = 8;
const FONT_SIZE = 2.5;
const HEADER_FONT_SIZE = 2.5;

const CIRCLED_NUMBERS = ['①','②','③','④','⑤','⑥','⑦','⑧','⑨','⑩',
  '⑪','⑫','⑬','⑭','⑮','⑯','⑰','⑱','⑲','⑳'];

/**
 * Wrap text to fit within maxWidth, handling Japanese and Latin characters.
 * Uses conservative character width estimates to prevent overflow.
 */
function wrapText(text: string, maxWidth: number, fontSize: number): string[] {
  if (!text) return [''];
  // Conservative width estimates — JP chars are wider than Latin
  const jpCharWidth = fontSize * 1.1;
  const latinCharWidth = fontSize * 0.6;
  const lines: string[] = [];
  let currentLine = '';
  let currentWidth = 0;

  for (const char of text) {
    const isJp = char.charCodeAt(0) > 0x2fff || (char.charCodeAt(0) >= 0x3000 && char.charCodeAt(0) <= 0x303f);
    const charW = isJp ? jpCharWidth : latinCharWidth;
    if (currentWidth + charW > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = char;
      currentWidth = charW;
    } else {
      currentLine += char;
      currentWidth += charW;
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines.length > 0 ? lines : [''];
}

/**
 * Truncate text to fit within maxWidth, adding ellipsis if needed.
 */
function truncateText(text: string, maxWidth: number, fontSize: number): string {
  if (!text) return '';
  const jpCharWidth = fontSize * 1.1;
  const latinCharWidth = fontSize * 0.6;
  let width = 0;
  let result = '';

  for (const char of text) {
    const isJp = char.charCodeAt(0) > 0x2fff || (char.charCodeAt(0) >= 0x3000 && char.charCodeAt(0) <= 0x303f);
    const charW = isJp ? jpCharWidth : latinCharWidth;
    if (width + charW > maxWidth - latinCharWidth * 3) { // Reserve space for "..."
      return result + '…';
    }
    result += char;
    width += charW;
  }
  return result;
}

export function BOMTable({
  x, y, width, maxHeight, bom, accessories = [], productInfo,
}: BOMTableProps) {
  const { t } = useTranslation('common');
  const clipId = useId().replace(/:/g, '_');

  const cols = Object.entries(COL_RATIOS).map(([key, ratio]) => ({
    key,
    w: width * ratio,
  }));
  let colX = 0;
  const colPositions = cols.map((col) => {
    const pos = colX;
    colX += col.w;
    return { ...col, x: pos };
  });

  const headers = [
    t('drawing.bomNo'),
    t('drawing.bomName'),
    t('drawing.bomSpec'),
    t('drawing.bomQty'),
    t('drawing.bomMaterial'),
    t('drawing.bomDimensions'),
    t('drawing.bomNotes'),
  ];

  const specColWidth = width * COL_RATIOS.spec - 2;
  const nameColWidth = width * COL_RATIOS.name - 2;
  const dimColWidth = width * COL_RATIOS.dimensions - 2;
  const notesColWidth = width * COL_RATIOS.notes - 2;

  const rowData = bom.map((entry, i) => {
    const specLines = wrapText(entry.specification, specColWidth, FONT_SIZE);
    const rowH = Math.max(ROW_HEIGHT, specLines.length * (FONT_SIZE + 0.8) + 2);
    return { entry, specLines, rowH, index: i };
  });

  let currentY = HEADER_HEIGHT;

  return (
    <g transform={`translate(${x}, ${y})`}>
      <defs>
        <clipPath id={`${clipId}-bom`}>
          <rect x={0} y={0} width={width} height={maxHeight} />
        </clipPath>
      </defs>

      <g clipPath={`url(#${clipId}-bom)`}>
        {/* Table border */}
        <rect x={0} y={0} width={width} height={maxHeight}
          fill="none" stroke={C.line} strokeWidth={0.5} />

        {/* Header row */}
        <rect x={0} y={0} width={width} height={HEADER_HEIGHT}
          fill="none" stroke={C.line} strokeWidth={0.3} />
        {colPositions.map((col, i) => (
          <g key={col.key}>
            {i > 0 && (
              <line x1={col.x} y1={0} x2={col.x} y2={maxHeight}
                stroke={C.line} strokeWidth={0.2} />
            )}
            <text
              x={col.x + col.w / 2}
              y={HEADER_HEIGHT / 2}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={HEADER_FONT_SIZE}
              fontFamily={JP_FONT}
              fill={C.text}
              fontWeight="500"
            >
              {headers[i]}
            </text>
          </g>
        ))}

        {/* Data rows */}
        {rowData.map(({ entry, specLines, rowH, index }) => {
          const rowY = currentY;
          currentY += rowH;
          if (currentY > maxHeight) return null;

          // Truncate text for non-wrapping columns
          const nameText = truncateText(entry.name, nameColWidth, FONT_SIZE);
          const dimText = truncateText(entry.dimensions ?? '', dimColWidth, FONT_SIZE);
          const notesText = truncateText(entry.notes ?? '', notesColWidth, FONT_SIZE);
          const materialText = truncateText(entry.material, width * COL_RATIOS.material - 2, FONT_SIZE);

          const values = [
            CIRCLED_NUMBERS[index] ?? `${index + 1}`,
            nameText,
            null, // spec column uses wrapped text
            String(entry.quantity),
            materialText,
            dimText,
            notesText,
          ];

          return (
            <g key={entry.number}>
              <line x1={0} y1={rowY} x2={width} y2={rowY}
                stroke={C.line} strokeWidth={0.2} />
              {colPositions.map((col, ci) => {
                if (ci === 2) {
                  // Specification column — multi-line wrap
                  return (
                    <text key={ci}
                      x={col.x + 1}
                      y={rowY + 2.5}
                      fontSize={FONT_SIZE}
                      fontFamily={JP_FONT}
                      fill={C.text}
                    >
                      {specLines.map((line, li) => (
                        <tspan key={li} x={col.x + 1} dy={li === 0 ? 0 : FONT_SIZE + 0.8}>
                          {line}
                        </tspan>
                      ))}
                    </text>
                  );
                }
                // Clip each cell individually to prevent overflow
                const cellClipId = `${clipId}-cell-${index}-${ci}`;
                return (
                  <g key={ci}>
                    <defs>
                      <clipPath id={cellClipId}>
                        <rect x={col.x} y={rowY} width={col.w} height={rowH} />
                      </clipPath>
                    </defs>
                    <text
                      x={col.x + col.w / 2}
                      y={rowY + rowH / 2}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize={FONT_SIZE}
                      fontFamily={JP_FONT}
                      fill={C.text}
                      clipPath={`url(#${cellClipId})`}
                    >
                      {values[ci]}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}

        {/* Accessories section */}
        {accessories.length > 0 && currentY + 10 < maxHeight && (
          <g>
            <line x1={0} y1={currentY} x2={width} y2={currentY}
              stroke={C.line} strokeWidth={0.3} />
            <text x={2} y={currentY + 3} fontSize={HEADER_FONT_SIZE}
              fontFamily={JP_FONT} fill={C.text} fontWeight="500"
              dominantBaseline="hanging">
              {t('drawing.attachments')}
            </text>
            {accessories.map((acc, i) => {
              const accY = currentY + HEADER_HEIGHT + i * ROW_HEIGHT;
              if (accY + ROW_HEIGHT > maxHeight) return null;
              return (
                <text key={i} x={4} y={accY + ROW_HEIGHT / 2}
                  fontSize={FONT_SIZE} fontFamily={JP_FONT} fill={C.text}
                  dominantBaseline="central">
                  {truncateText(`${acc.name}　${acc.quantity}個　${acc.specification}`, width - 8, FONT_SIZE)}
                </text>
              );
            })}
          </g>
        )}

        {/* Product info */}
        {productInfo?.weight && currentY + 20 < maxHeight && (() => {
          const infoY = currentY + (accessories.length > 0 ? HEADER_HEIGHT + accessories.length * ROW_HEIGHT : 0) + 4;
          return (
            <g>
              <line x1={0} y1={infoY} x2={width} y2={infoY}
                stroke={C.line} strokeWidth={0.3} />
              <text x={2} y={infoY + 4} fontSize={FONT_SIZE}
                fontFamily={JP_FONT} fill={C.text} dominantBaseline="hanging">
                {t('drawing.weight')}{productInfo.weight}
              </text>
              {productInfo.packagingDimensions?.map((dim, i) => (
                <text key={i} x={2} y={infoY + 4 + (i + 1) * (FONT_SIZE + 2)}
                  fontSize={FONT_SIZE} fontFamily={JP_FONT} fill={C.text}
                  dominantBaseline="hanging">
                  {truncateText(dim, width - 4, FONT_SIZE)}
                </text>
              ))}
            </g>
          );
        })()}
      </g>
    </g>
  );
}
